/**
 * Pure encode/parse logic for the Communicator deep-link state — the slice of
 * view state the communicator persists in the URL so a reload (or a shared
 * link) restores the same group, context (student / group channels), tab and
 * conversation preselect.
 *
 * Deliberately decoupled from React/Apollo/`react-router` (same design as
 * `groupDeepLink`) so round-trip and totality can be property-tested in
 * isolation. `useCommunicatorDeepLink` is the thin React wrapper.
 *
 * Conventions:
 *   - `slack` is the default tab and is **omitted** from the URL.
 *   - Every field omits its default/empty value from the encoded params.
 *   - Parsing is **total**: each field falls back to its own safe default on
 *     unrecognized input, so {@link parse} never throws.
 *   - Parsed state is **normalized** (see {@link normalize}): `student` and
 *     `ctx=channels` are mutually exclusive (student wins), context fields are
 *     meaningless without a selected group, and the mail tab does not exist in
 *     the channels context.
 */

/** Main-area tab. `mail` is unavailable in the channels context. */
export type CommunicatorTab = 'slack' | 'mail' | 'tasks';

/** Middle-column context: '' = whole group / none, 'channels' = group channels. */
export type CommunicatorCtx = '' | 'channels';

/** URL-encoded view state that survives a reload. */
export interface ICommunicatorDeepLinkState {
  /** Selected group id. Default `''` (nothing selected). */
  group: string;
  /** Selected student id. Default `''`. Mutually exclusive with `ctx`. */
  student: string;
  /** `'channels'` when the pinned "group channels" item is selected. */
  ctx: CommunicatorCtx;
  /** Active tab. Default `slack`, represented by an absent `tab` param. */
  tab: CommunicatorTab;
  /** Conversation preselect inside the active context. Opaque id. */
  conv: string;
  /** Groups-column search query. Default `''`. */
  query: string;
}

const PARAM = {
  group: 'group',
  student: 'student',
  ctx: 'ctx',
  tab: 'tab',
  conv: 'conv',
  query: 'q',
} as const;

const DEFAULT_TAB: CommunicatorTab = 'slack';
const TAB_IDS: readonly CommunicatorTab[] = ['slack', 'mail', 'tasks'];
const CTX_CHANNELS: CommunicatorCtx = 'channels';

const isTab = (value: string | null): value is CommunicatorTab =>
  value !== null && (TAB_IDS as readonly string[]).includes(value);

/**
 * Enforce the state invariants; parse() always returns a normalized state and
 * encode() expects one (a non-normalized state still encodes, but round-trips
 * to its normalized form):
 *   1. Without a group there is no context: student/ctx/conv reset.
 *   2. `student` and `ctx=channels` are mutually exclusive — student wins.
 *   3. The channels context has no mail tab — `mail` falls back to `slack`.
 */
export const normalize = (
  state: ICommunicatorDeepLinkState,
): ICommunicatorDeepLinkState => {
  const { group } = state;
  const student = group ? state.student : '';
  const ctx: CommunicatorCtx =
    group && !student && state.ctx === CTX_CHANNELS ? CTX_CHANNELS : '';
  const conv = group ? state.conv : '';
  const tab: CommunicatorTab =
    ctx === CTX_CHANNELS && state.tab === 'mail' ? DEFAULT_TAB : state.tab;
  return { group, student, ctx, tab, conv, query: state.query };
};

/**
 * Encode a deep-link state into {@link URLSearchParams}, omitting defaults
 * (including the `slack` tab convention). `parse(encode(state))` restores an
 * equal state for any normalized input.
 */
export const encode = (state: ICommunicatorDeepLinkState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.group) params.set(PARAM.group, state.group);
  if (state.student) params.set(PARAM.student, state.student);
  if (state.ctx) params.set(PARAM.ctx, state.ctx);
  if (state.tab !== DEFAULT_TAB) params.set(PARAM.tab, state.tab);
  if (state.conv) params.set(PARAM.conv, state.conv);
  if (state.query) params.set(PARAM.query, state.query);

  return params;
};

/**
 * Parse a query string (or {@link URLSearchParams}) into a normalized
 * deep-link state. Total and defensive: never throws, every field falls back
 * to its default on unrecognized input.
 */
export const parse = (
  search: string | URLSearchParams,
): ICommunicatorDeepLinkState => {
  const params = new URLSearchParams(search);

  const rawTab = params.get(PARAM.tab);
  const rawCtx = params.get(PARAM.ctx);

  return normalize({
    group: params.get(PARAM.group) ?? '',
    student: params.get(PARAM.student) ?? '',
    ctx: rawCtx === CTX_CHANNELS ? CTX_CHANNELS : '',
    tab: isTab(rawTab) ? rawTab : DEFAULT_TAB,
    conv: params.get(PARAM.conv) ?? '',
    query: params.get(PARAM.query) ?? '',
  });
};
